import React, { createContext, useCallback, useContext, useState } from 'react';
import { SquiggleItem } from '../types/types';

interface OpenItemsContextProps {
  itemsOpen: SquiggleItem[];
  addOpenItem: (item: SquiggleItem) => void;
  closeOpenItem: (itemId: string) => void;
  clearOpenNonEditItems: () => void;
  reconstituteOpenItems: (
    quests: SquiggleItem[],
    peopleAndOrgs: SquiggleItem[],
    places: SquiggleItem[],
    misc: SquiggleItem[],
    logEntries: SquiggleItem[],
    system: SquiggleItem[],
    extraItemId?: string
  ) => SquiggleItem[];
  setEditProtection: (itemId: string, value: boolean) => void;
  hasEditProtection: () => boolean;
  editProtectionIds: string[];
  setSelectedItemId: (itemId: string|null) => void;
  selectedItemId: string|null;
}

let editProtectionIds: string[] = [];

const reconstituteItemsOpen = (
  setItemsOpen: React.Dispatch<React.SetStateAction<SquiggleItem[]>>,
  setSelectedItemId: React.Dispatch<React.SetStateAction<string|null>>,
  quests: SquiggleItem[],
  peopleAndOrgs: SquiggleItem[],
  places: SquiggleItem[],
  misc: SquiggleItem[],
  logEntries: SquiggleItem[],
  system: SquiggleItem[],
  extraItemId?: string
) => {
  const allItems = [...quests, ...peopleAndOrgs, ...places, ...misc, ...logEntries, ...system];
  const extraItems = (extraItemId ? [{ id: extraItemId }] : []);
  setItemsOpen((itemsOpen: SquiggleItem[]) => {
    const updatedAndPreserved = [...itemsOpen, ...extraItems]
      .map(oldItem => {
        const isEditProtected = !!editProtectionIds.find(id => id === oldItem.id);
        if (isEditProtected) {
          return oldItem as SquiggleItem;
        }
        return allItems.find(newItem => newItem.id === oldItem.id);
      })
      .filter((item): item is SquiggleItem => item !== undefined);
    setSelectedItemId(currentSelectedItemId => (
      currentSelectedItemId && !updatedAndPreserved.find(item => item.id === currentSelectedItemId)
        ? null
        : currentSelectedItemId
    ));
    return updatedAndPreserved;
  });
  return allItems;
};

const OpenItemsContext = createContext<OpenItemsContextProps | undefined>(undefined);

export const OpenItemsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [itemsOpen, setItemsOpen] = useState<SquiggleItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string|null>(null);

  const addOpenItem = useCallback((item: SquiggleItem) => {
    setItemsOpen((current) => {
      if (current.find((openItem) => openItem.id === item.id)) {
        return current;
      }
      return [...current, item];
    });
  }, [setItemsOpen]);

  const closeOpenItem = useCallback((itemId: string) => {
    setItemsOpen((current) => current.filter((item) => item.id !== itemId));
  }, [setItemsOpen]);

  const clearOpenNonEditItems = useCallback(() => {
    setItemsOpen((current) => current.filter(item => editProtectionIds.find(id => id === item.id)));
  }, [setItemsOpen]);

  const reconstituteOpenItems = useCallback((
    quests: SquiggleItem[],
    peopleAndOrgs: SquiggleItem[],
    places: SquiggleItem[],
    misc: SquiggleItem[],
    logEntries: SquiggleItem[],
    system: SquiggleItem[],
    extraItemId?: string) => reconstituteItemsOpen(
      setItemsOpen,
      setSelectedItemId,
      quests,
      peopleAndOrgs,
      places,
      misc,
      logEntries,
      system,
      extraItemId), [setItemsOpen, setSelectedItemId]);

  const setEditProtection = useCallback((itemId: string, value: boolean) => {
    if (value) {
      if (!editProtectionIds.find(id => id === itemId)) {
        editProtectionIds.push(itemId);
      }
    } else {
      editProtectionIds = editProtectionIds.filter(id => id !== itemId);
    }
  }, []);

  const hasEditProtection = useCallback(() => editProtectionIds.length > 0, []);

  const setSelectedItemIdInternal = useCallback((itemId: string|null) => {
    setSelectedItemId(itemId);
    window.location.hash = itemId ? itemId : '';
  }, [setSelectedItemId]);

  return (
    <OpenItemsContext.Provider value={{
      itemsOpen,
      addOpenItem,
      closeOpenItem,
      clearOpenNonEditItems,
      reconstituteOpenItems,
      setEditProtection,
      hasEditProtection,
      editProtectionIds,
      setSelectedItemId: setSelectedItemIdInternal,
      selectedItemId
     }}>
      {children}
    </OpenItemsContext.Provider>
  );
};

export const useOpenItemsContext = (): OpenItemsContextProps => {
  const context = useContext(OpenItemsContext);
  if (!context) {
    throw new Error('useOpenItemsContext must be used within an OpenItemsProvider');
  }
  return context;
};
